import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import ConversationList from './components/ConversationList';
import ChatWindow from './components/ChatWindow';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const seededHistoryRef = useRef(false);
  const [token, setToken] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const refreshInFlightRef = useRef(null);
  const [appName, setAppName] = useState('LgoKnowledge');
  const [themeColor, setThemeColor] = useState('#c9a227');
  const [conversations, setConversations] = useState([]);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState('LgoKnowledge');
  const [settingsColor, setSettingsColor] = useState('#c9a227');
  const [settingsSmsLimit, setSettingsSmsLimit] = useState(10);
  const [csrfToken, setCsrfToken] = useState('');
  const [appVersion, setAppVersion] = useState('v0.0.0');

  const routeState = useMemo(() => {
    const path = location.pathname;
    if (path === '/new') {
      return { isNew: true };
    }
    if (path.startsWith('/chat/')) {
      const phone = decodeURIComponent(path.slice('/chat/'.length));
      return { isNew: false, phone };
    }
    return null;
  }, [location.pathname]);

  const selectedCustomer = useMemo(() => {
    if (!routeState) return null;
    if (routeState.isNew) {
      return { phone: '', name: '', isNew: true };
    }
    const match = conversations.find((conv) => conv.phone === routeState.phone);
    if (match) return match;
    return { phone: routeState.phone, name: '', isNew: false };
  }, [routeState, conversations]);

  useEffect(() => {
    if (
      !seededHistoryRef.current &&
      location.pathname !== '/' &&
      !location.state?.appHistory
    ) {
      seededHistoryRef.current = true;
      navigate('/', { replace: false, state: { appHistory: true, seeded: true } });
      navigate(location.pathname, { replace: false, state: { appHistory: true } });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (token) {
      fetchConversations();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchConversations, 5000);
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchSettings();
  }, [token]);

  useEffect(() => {
    if (token) return;
    fetchPublicSettings();
  }, [token]);

  useEffect(() => {
    fetchVersion();
  }, []);

  useEffect(() => {
    if (!token) return;
    const path = location.pathname;
    const isValid =
      path === '/' ||
      path === '/new' ||
      path.startsWith('/chat/');
    if (!isValid) {
      navigate('/', { state: { appHistory: true } });
    }
  }, [token, location.pathname, navigate]);

  useEffect(() => {
    if (!token) return;
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);
    if (!supported) return;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (existing && Notification.permission === 'granted') {
          setPushEnabled(true);
        }
      } catch (error) {
        console.error('Error checking push subscription:', error);
      }
    })();
  }, [token]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch('/api/conversations', {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return fetchConversations();
        handleLogout();
        return null;
      }
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
    return null;
  };

  const attemptRefresh = async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    refreshInFlightRef.current = (async () => {
      try {
        const response = await fetch('/api/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          return false;
        }
        await response.json();
        if (token !== true) setToken(true);
        return true;
      } catch (error) {
        console.error('Error refreshing token:', error);
        return false;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();
    return refreshInFlightRef.current;
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings', {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return fetchSettings();
        handleLogout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setAppName(data.appName || 'LgoKnowledge');
        setThemeColor(data.themeColor || '#c9a227');
        if (Number.isFinite(data.smsMaxPerDestinationPerHour)) {
          setSettingsSmsLimit(data.smsMaxPerDestinationPerHour);
        }
        setToken(true);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPublicSettings = async () => {
    try {
      const response = await fetch('/api/settings/public', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAppName(data.appName || 'LgoKnowledge');
        setThemeColor(data.themeColor || '#c9a227');
      }
    } catch (error) {
      console.error('Error fetching public settings:', error);
    }
  };

  const getCsrfToken = () => csrfToken || getCookieValue('csrf_token') || '';

  const getCookieValue = (name) => {
    const value = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return value ? decodeURIComponent(value.split('=').slice(1).join('=')) : '';
  };

  const checkSession = async () => {
    try {
      const response = await fetch('/api/settings', {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAppName(data.appName || 'LgoKnowledge');
        setThemeColor(data.themeColor || '#c9a227');
        if (Number.isFinite(data.smsMaxPerDestinationPerHour)) {
          setSettingsSmsLimit(data.smsMaxPerDestinationPerHour);
        }
        setCsrfToken(getCookieValue('csrf_token'));
        setToken(true);
        setSessionChecked(true);
        return;
      }
    } catch (error) {
      console.error('Error checking session:', error);
    }
    setToken(false);
    setSessionChecked(true);
  };

  const fetchVersion = async () => {
    try {
      const response = await fetch('/version', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAppVersion(data.version || 'v0.0.0');
      }
    } catch (error) {
      console.error('Error fetching version:', error);
    }
  };

  const refreshAllData = async () => {
    await fetchConversations();
    setRefreshTick((tick) => tick + 1);
  };

  useEffect(() => {
    if (!token) return undefined;
    const handleFocus = () => {
      refreshAllData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token]);

  const handleLogin = () => {
    setToken(true);
    setCsrfToken(getCookieValue('csrf_token'));
    navigate('/', { state: { appHistory: true } });
  };

  const handleLogout = () => {
    fetch('/api/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRF-Token': getCsrfToken()
      }
    }).catch(() => {});
    setToken(false);
    navigate('/', { state: { appHistory: true } });
  };

  const handleAuthError = async () => {
    const refreshed = await attemptRefresh();
    if (!refreshed) {
      handleLogout();
      return false;
    }
    return true;
  };

  const handleSelectCustomer = (customer) => {
    if (!customer?.phone) return;
    navigate(`/chat/${encodeURIComponent(customer.phone)}`, { state: { appHistory: true } });
  };

  const handleNewConversation = () => {
    navigate('/new', { state: { appHistory: true } });
  };

  const handleBackToConversations = () => {
    navigate('/', { state: { appHistory: true } });
  };

  const handleDeleteConversation = async (customer) => {
    if (!customer?.phone) return;
    const confirmed = window.confirm('Delete this conversation and all messages?');
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/conversations/${encodeURIComponent(customer.phone)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return handleDeleteConversation(customer);
        handleLogout();
        return;
      }
      if (response.ok) {
        if (selectedCustomer?.phone === customer.phone) {
          navigate('/', { state: { appHistory: true } });
        }
        fetchConversations();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleEnableNotifications = async () => {
    if (!pushSupported || pushLoading) return;
    setPushLoading(true);

    try {
      if (Notification.permission === 'denied') {
        alert('Notifications are blocked in your browser settings.');
        return;
      }

      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return;
        }
      }

      const keyResponse = await fetch('/api/push/public-key', {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-store'
        }
      });
      if (keyResponse.status === 401 || keyResponse.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return handleEnableNotifications();
        handleLogout();
        return;
      }
      if (!keyResponse.ok) {
        throw new Error('Push key unavailable');
      }
      const { publicKey } = await keyResponse.json();

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      const saveResponse = await fetch('/api/push/subscribe', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ subscription })
      });

      if (saveResponse.status === 401 || saveResponse.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return handleEnableNotifications();
        handleLogout();
        return;
      }
      if (!saveResponse.ok) {
        throw new Error('Failed to save subscription');
      }

      setPushEnabled(true);
    } catch (error) {
      console.error('Error enabling notifications:', error);
      alert('Unable to enable notifications. Please try again.');
    } finally {
      setPushLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refreshAllData();
    } finally {
      setRefreshing(false);
    }
  };

  const toggleMenu = () => {
    setMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const openSettings = () => {
    setSettingsName(appName);
    setSettingsColor(themeColor);
    setSettingsSmsLimit((prev) => (Number.isFinite(prev) ? prev : 10));
    setSettingsOpen(true);
  };

  const closeSettings = () => {
    setSettingsOpen(false);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    const trimmed = settingsName.trim();
    const nextName = trimmed || 'LgoKnowledge';
    const nextColor = settingsColor || '#c9a227';
    const nextSmsLimit = Number(settingsSmsLimit);
    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          appName: nextName,
          themeColor: nextColor,
          smsMaxPerDestinationPerHour: nextSmsLimit
        })
      });
      if (response.status === 401 || response.status === 403) {
        const refreshed = await attemptRefresh();
        if (refreshed) return handleSaveSettings(e);
        handleLogout();
        return;
      }
      if (response.ok) {
        setAppName(nextName);
        setThemeColor(nextColor);
        if (Number.isFinite(nextSmsLimit) && nextSmsLimit > 0) {
          setSettingsSmsLimit(Math.floor(nextSmsLimit));
        }
        setSettingsOpen(false);
      } else {
        const data = await response.json();
        alert(`Error saving settings: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    }
  };

  const clamp = (value) => Math.max(0, Math.min(255, value));

  const adjustColor = (hex, amount) => {
    const raw = hex.replace('#', '');
    if (raw.length !== 6) return hex;
    const r = clamp(parseInt(raw.slice(0, 2), 16) + amount);
    const g = clamp(parseInt(raw.slice(2, 4), 16) + amount);
    const b = clamp(parseInt(raw.slice(4, 6), 16) + amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const toRgba = (hex, alpha) => {
    const raw = hex.replace('#', '');
    if (raw.length !== 6) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  useEffect(() => {
    const base = themeColor || '#c9a227';
    document.documentElement.style.setProperty('--gold-start', base);
    document.documentElement.style.setProperty('--gold-end', adjustColor(base, 30));
    document.documentElement.style.setProperty('--gold-accent', adjustColor(base, -20));
    document.documentElement.style.setProperty('--gold-soft', adjustColor(base, 90));
    document.documentElement.style.setProperty('--gold-shadow', toRgba(base, 0.4));
  }, [themeColor]);

  const normalizePhoneForMatch = (input) => {
    if (!input) return '';
    const trimmed = String(input).trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return '';
    if (hasPlus) return `+${digits}`;
    if (digits.startsWith('44')) return `+${digits}`;
    if (digits.length === 11 && digits.startsWith('07')) return `+44${digits.slice(1)}`;
    if (digits.length === 10 && digits.startsWith('7')) return `+44${digits}`;
    return digits;
  };

  if (!sessionChecked) {
    return null;
  }

  if (!token) {
    return <Login onLogin={handleLogin} appName={appName} />;
  }

  return (
    <div className="App">
      <header className="app-header">
        <h1>
          <a className="app-title" href="/">
            {appName} SMS
          </a>
        </h1>
        <div className="app-actions">
          <button
            type="button"
            className="menu-toggle"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={toggleMenu}
          >
            <span className="menu-bar" />
            <span className="menu-bar" />
            <span className="menu-bar" />
          </button>
          {menuOpen ? (
            <div className="menu-panel" role="menu">
              {pushSupported ? (
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    handleEnableNotifications();
                    closeMenu();
                  }}
                  disabled={pushEnabled || pushLoading}
                >
                  {pushEnabled ? 'Notifications On' : pushLoading ? 'Enabling...' : 'Enable Notifications'}
                </button>
              ) : null}
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  handleRefresh();
                  closeMenu();
                }}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  closeMenu();
                  openSettings();
                }}
              >
                Settings
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  handleLogout();
                  closeMenu();
                }}
              >
                Logout
              </button>
            </div>
          ) : null}
        </div>
      </header>
      <div className="app-container">
        <ConversationList
          conversations={conversations}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={handleSelectCustomer}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
        />
        <ChatWindow
          customer={selectedCustomer}
          token={token}
          onMessageSent={async (phone) => {
            const data = await fetchConversations();
            if (phone && data?.length) {
              const normalized = normalizePhoneForMatch(phone);
              const match = data.find((conv) => normalizePhoneForMatch(conv.phone) === normalized);
              const routePhone = match?.phone || phone;
              navigate(`/chat/${encodeURIComponent(routePhone)}`, { state: { appHistory: true } });
            }
          }}
          onBack={handleBackToConversations}
          refreshTick={refreshTick}
          onAuthError={handleAuthError}
          appName={appName}
        />
      </div>
      {settingsOpen ? (
        <div className="settings-backdrop" role="presentation" onClick={closeSettings}>
          <div
            className="settings-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Settings</h3>
            <div className="settings-version">Version {appVersion}</div>
            <form onSubmit={handleSaveSettings}>
              <label className="settings-label">System Name</label>
              <input
                type="text"
                value={settingsName}
                onChange={(e) => setSettingsName(e.target.value)}
                className="settings-input"
                placeholder="System name"
              />
              <label className="settings-label">Theme Color</label>
              <input
                type="color"
                value={settingsColor}
                onChange={(e) => setSettingsColor(e.target.value)}
                className="settings-color"
              />
              <label className="settings-label">Max Messages per Destination (per hour)</label>
              <input
                type="number"
                min="1"
                value={settingsSmsLimit}
                onChange={(e) => setSettingsSmsLimit(Number(e.target.value))}
                className="settings-input"
              />
              <div className="settings-actions">
                <button type="button" className="settings-secondary" onClick={closeSettings}>
                  Cancel
                </button>
                <button type="submit" className="settings-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
